/**
 * decisionRationale — produces per-compound structured "why this rank"
 * records for the results-interpretation layer.
 *
 * Two callers:
 *   - generateFor(compound, context)
 *       Synchronous, deterministic template. Default fast path used
 *       on first paint of ResultsPane / ChatBot. No LLM call.
 *   - generateGroundedFor(compound, context, options)
 *       Async, LLM-grounded prose for the same DecisionRationale
 *       schema. Replaces only the two prose fields (rankExplanation,
 *       suggestedNext); the four numeric fields stay deterministic.
 *       Triggered opt-in via the "explain in more detail" UI button.
 *       Falls back to the template result on any LLM failure.
 *
 * The output shape is published as a JSON schema alongside the
 * utility so consumers (ResultsPane, ResultCard) can rely on stable
 * fields.
 *
 * @module gibbs/results/decisionRationale
 */

// eslint-disable-next-line global-require
import schemaDoc from './decisionRationale.schema.json';
import { SYSTEM_PROMPT, buildUserPrompt } from './rationalePromptTemplates';

/**
 * Exported schema document. Consumers use this to validate rationale
 * objects they produce or consume.
 */
export const DECISION_RATIONALE_SCHEMA = Object.freeze(schemaDoc);

/**
 * @typedef {Object} Flag
 * @property {string} id
 * @property {'info'|'warning'|'severe'} severity
 * @property {string|null} citation
 */

/**
 * @typedef {Object} DecisionRationale
 * @property {number} compoundId
 * @property {string} rankExplanation
 * @property {Object|null} energyBreakdown
 * @property {Object|null} interactionSummary
 * @property {Flag[]} flags
 * @property {string|null} suggestedNext
 */

const MAX_EXPLANATION = 200;

function truncate(s, n) {
    if (typeof s !== 'string') return '';
    if (s.length <= n) return s;
    return `${s.slice(0, n - 1)}…`;
}

function buildEnergyBreakdown(energies) {
    if (!energies || typeof energies !== 'object') return null;
    const fields = ['vdw', 'electrostatic', 'total', 'dockingScore', 'gninaScore'];
    const anyPresent = fields.some(
        (f) => typeof energies[f] === 'number' && Number.isFinite(energies[f])
    );
    if (!anyPresent) return null;
    const out = {};
    for (const f of fields) {
        const v = energies[f];
        out[f] = typeof v === 'number' && Number.isFinite(v) ? v : null;
    }
    return out;
}

function buildInteractionSummary(interactions) {
    if (!interactions || typeof interactions !== 'object') return null;
    const fields = ['hydrogenBonds', 'hydrophobicContacts', 'piStacking'];
    const anyPresent = fields.some(
        (f) => typeof interactions[f] === 'number' && interactions[f] > 0
    ) || (Array.isArray(interactions.keyResidues) && interactions.keyResidues.length > 0);
    if (!anyPresent) return null;
    return {
        hydrogenBonds: Math.max(0, Math.trunc(interactions.hydrogenBonds || 0)),
        hydrophobicContacts: Math.max(0, Math.trunc(interactions.hydrophobicContacts || 0)),
        piStacking: Math.max(0, Math.trunc(interactions.piStacking || 0)),
        keyResidues: Array.isArray(interactions.keyResidues)
            ? interactions.keyResidues.filter((r) => typeof r === 'string')
            : [],
    };
}

function normalizeFlags(flagsIn) {
    if (!Array.isArray(flagsIn)) return [];
    return flagsIn.map((f) => {
        if (typeof f === 'string') {
            return { id: f, severity: 'warning', citation: null };
        }
        const id = (f && typeof f.id === 'string' && f.id) || 'unknown';
        const severity = (f && ['info', 'warning', 'severe'].includes(f.severity))
            ? f.severity : 'warning';
        const citation = f && typeof f.citation === 'string' && f.citation
            ? f.citation : null;
        return { id, severity, citation };
    });
}

/**
 * Compose a short rank-explanation sentence from the evidence available.
 * Must reference at least one concrete value (score, interaction, flag)
 * when any exist; must explicitly note the absence when none do.
 *
 * @param {Object} compound
 * @param {number|null|undefined} rank
 * @param {Object|null} energyBreakdown
 * @param {Object|null} interactionSummary
 * @param {Flag[]} flags
 * @returns {string}
 */
function composeRankExplanation(compound, rank, energyBreakdown, interactionSummary, flags) {
    const pieces = [];

    // Promote the best available score
    if (energyBreakdown) {
        if (energyBreakdown.gninaScore != null) {
            pieces.push(`GNINA ${energyBreakdown.gninaScore.toFixed(1)}`);
        } else if (energyBreakdown.dockingScore != null) {
            pieces.push(`dock ${energyBreakdown.dockingScore.toFixed(1)}`);
        } else if (energyBreakdown.total != null) {
            pieces.push(`E ${energyBreakdown.total.toFixed(1)}`);
        }
    }

    if (interactionSummary) {
        const parts = [];
        if (interactionSummary.hydrogenBonds > 0) {
            parts.push(`${interactionSummary.hydrogenBonds} H-bond${interactionSummary.hydrogenBonds === 1 ? '' : 's'}`);
        }
        if (interactionSummary.piStacking > 0) {
            parts.push(`${interactionSummary.piStacking} π-stack`);
        }
        if (parts.length) pieces.push(parts.join(', '));
    }

    if (flags.length > 0) {
        const severe = flags.filter((f) => f.severity === 'severe').length;
        const warn = flags.filter((f) => f.severity === 'warning').length;
        if (severe > 0) pieces.push(`${severe} severe alert${severe === 1 ? '' : 's'}`);
        else if (warn > 0) pieces.push(`${warn} warning flag${warn === 1 ? '' : 's'}`);
    }

    if (pieces.length === 0) {
        return 'No scores, interactions, or alerts computed — rank reflects compound index only.';
    }

    const rankPart = typeof rank === 'number' ? `Rank ${rank}` : 'Rank';
    return truncate(`${rankPart}: ${pieces.join('; ')}.`, MAX_EXPLANATION);
}

/**
 * Compose a one-line next-step suggestion.
 *
 * @param {Object} compound
 * @param {Object|null} energyBreakdown
 * @param {Flag[]} flags
 * @returns {string|null}
 */
function composeSuggestedNext(compound, energyBreakdown, flags) {
    if (!energyBreakdown) {
        return 'Score this compound with `dock-compound` or `predict-gnina-scores` to produce ranking evidence.';
    }
    const severe = flags.filter((f) => f.severity === 'severe').length;
    if (severe > 0) {
        return 'Address severe structural alerts before progressing — consider `grow-from-all-atoms` to modify alerting substructures.';
    }
    if (energyBreakdown.gninaScore != null && energyBreakdown.gninaScore > 0) {
        return 'Score is poor; try `energy-minimize` and re-score, or `grow-from-all-atoms` for better analogs.';
    }
    return 'Good starting point — `grow-from-all-atoms` to enumerate analogs, or `predict-admet` to check properties.';
}

/**
 * Build a DecisionRationale record for a single compound.
 *
 * @param {Object} compound - A ranker RankedCompound or a getCompoundData entry
 *   augmented with flags + interactionCount + interactions
 * @param {Object} [context] - Extra context
 * @param {number} [context.rank] - The compound's rank (1-indexed)
 * @param {Object} [context.interactions] - ProLIF-shaped interaction record
 * @returns {DecisionRationale}
 */
function pickCompoundId(c) {
    if (c && typeof c.compoundId === 'number') return c.compoundId;
    if (c && typeof c.id === 'number') return c.id;
    return 0;
}

export function generateFor(compound, context = {}) {
    const compoundId = pickCompoundId(compound);

    const energySource = compound && (compound.energies || compound.secondaryScores);
    const energyBreakdown = buildEnergyBreakdown(energySource);
    const interactionSummary = buildInteractionSummary(context.interactions);
    const flags = normalizeFlags(compound && compound.flags);
    const rankExplanation = composeRankExplanation(
        compound, context.rank, energyBreakdown, interactionSummary, flags
    );
    const suggestedNext = composeSuggestedNext(compound, energyBreakdown, flags);

    return {
        compoundId,
        rankExplanation,
        energyBreakdown,
        interactionSummary,
        flags,
        suggestedNext,
    };
}

// ─── LLM-grounded variant ──────────────────────────────────────────────────

const MAX_SUGGESTED_NEXT = 300;

/**
 * Default model id for the rationale-grounding tier. Mirrors the
 * `text-to-action` tier convention. Override via options.model in
 * `generateGroundedFor` if a caller needs a different model.
 */
const DEFAULT_MODEL = 'gpt-4o-mini';

/**
 * Build the universe of values the LLM is allowed to mention. Used by
 * the anti-hallucination guard to verify every numeric / identifier
 * appearance in the LLM prose traces back to the structured input.
 *
 * @param {DecisionRationale} templateResult
 * @returns {{
 *   numbers: number[],
 *   compoundIds: number[],
 *   residues: string[],
 *   flagIds: string[],
 *   actionIds: Set<string>,
 * }}
 */
function buildAllowedVocabulary(templateResult) {
    const numbers = [];
    const eb = templateResult.energyBreakdown;
    if (eb) {
        for (const k of ['vdw', 'electrostatic', 'total', 'dockingScore', 'gninaScore']) {
            if (typeof eb[k] === 'number' && Number.isFinite(eb[k])) numbers.push(eb[k]);
        }
    }
    const is = templateResult.interactionSummary;
    if (is) {
        for (const k of ['hydrogenBonds', 'hydrophobicContacts', 'piStacking']) {
            if (typeof is[k] === 'number') numbers.push(is[k]);
        }
    }
    return {
        numbers,
        compoundIds: [templateResult.compoundId],
        residues: (is && Array.isArray(is.keyResidues)) ? is.keyResidues : [],
        flagIds: (templateResult.flags || []).map((f) => f.id),
        // ActionIds the prose may reference are limited to the
        // canonical Gibbs action set the suggestedNext composer uses.
        // The list is intentionally loose — the LLM is allowed to
        // suggest any registered action by id; enforcement is via
        // length + tone, not a closed allowlist.
        actionIds: null,
    };
}

/**
 * Anti-hallucination guard. Returns null on pass; returns an error
 * string naming the offending claim on fail.
 *
 * Numeric check: every signed-decimal token in the prose must match
 * (within 0.05 absolute tolerance for floating-point output) one of
 * the numeric values in the allowed vocabulary OR be a small integer
 * count present in interactionSummary OR be the rank/compoundId.
 *
 * Compound-id check: every "Compound N" reference in the prose must
 * match the input's compoundId.
 *
 * Residue check: every Xxx-NNN-style residue mention (3-letter +
 * dash + digits) must appear in keyResidues.
 *
 * @param {string} prose - LLM-emitted prose (rankExplanation + suggestedNext)
 * @param {ReturnType<typeof buildAllowedVocabulary>} vocab
 * @param {number|null} rank - The rank this compound is at
 * @returns {string|null} error reason, or null if no hallucination
 */
function detectHallucination(prose, vocab, rank) {
    if (typeof prose !== 'string' || prose.length === 0) return null;

    // Residue mention check FIRST — three-letter amino acid + dash +
    // 1–4 digits. Flag any residue not in keyResidues; this also
    // gives us a list of allowed residue tokens to strip before the
    // numeric scan so the digits inside residue names (e.g., the 101
    // in "Asn-101") don't get re-flagged as fabricated values.
    const residueMatches = prose.match(/\b[A-Z][a-z]{2}-\d{1,4}\b/g) || [];
    if (residueMatches.length > 0) {
        const allowed = new Set(vocab.residues);
        for (const r of residueMatches) {
            if (!allowed.has(r)) {
                return `residue not in keyResidues: ${r}`;
            }
        }
    }

    // Compound ID check — "Compound N" patterns. Done BEFORE numeric
    // extraction so the digits inside an allowed "Compound N" mention
    // get stripped from the numeric scan too.
    const compoundMatches = prose.match(/\bCompound\s+(\d+)/g) || [];
    for (const m of compoundMatches) {
        const id = parseInt(m.match(/\d+/)[0], 10);
        if (!vocab.compoundIds.includes(id)) {
            return `compound id not in input: Compound ${id}`;
        }
    }

    // Strip allowed residue + compound mentions from the prose so the
    // digits they contain don't collide with the numeric check.
    let stripped = prose;
    for (const r of residueMatches) stripped = stripped.split(r).join(' ');
    for (const m of compoundMatches) stripped = stripped.split(m).join(' ');

    // Numeric check — extract every signed-decimal token from the
    // stripped prose. Pattern: optional minus, digits, optional
    // decimal+digits.
    const numericMatches = stripped.match(/-?\d+(?:\.\d+)?/g) || [];
    const allowedNumbers = new Set();
    for (const n of vocab.numbers) allowedNumbers.add(n);
    if (typeof rank === 'number') allowedNumbers.add(rank);
    for (const id of vocab.compoundIds) allowedNumbers.add(id);

    for (const tok of numericMatches) {
        const val = Number(tok);
        if (!Number.isFinite(val)) continue;
        // Allow if the value matches any allowed number within tolerance.
        let ok = false;
        for (const allowed of allowedNumbers) {
            if (Math.abs(val - allowed) <= 0.05) { ok = true; break; }
        }
        // Also allow plain small integers 0–10 that map to interaction
        // counts (the prose often says "3 H-bonds" where the count is
        // already in numbers, but bare small integers are otherwise
        // hard to forbid without false positives on rank ordinals).
        if (!ok && Number.isInteger(val) && val >= 0 && val <= 10) ok = true;
        if (!ok) return `numeric value not in input: ${tok}`;
    }

    return null;
}

/**
 * Validate the LLM-emitted prose object against the schema.
 *
 * @param {*} obj - Parsed LLM JSON
 * @returns {string|null} error reason, or null if valid
 */
function validateLLMProse(obj) {
    if (!obj || typeof obj !== 'object') return 'response is not an object';
    if (typeof obj.rankExplanation !== 'string') return 'rankExplanation missing or not a string';
    if (typeof obj.suggestedNext !== 'string') return 'suggestedNext missing or not a string';
    if (obj.rankExplanation.length < 1 || obj.rankExplanation.length > MAX_EXPLANATION) {
        return `rankExplanation length ${obj.rankExplanation.length} out of [1, ${MAX_EXPLANATION}]`;
    }
    if (obj.suggestedNext.length < 1 || obj.suggestedNext.length > MAX_SUGGESTED_NEXT) {
        return `suggestedNext length ${obj.suggestedNext.length} out of [1, ${MAX_SUGGESTED_NEXT}]`;
    }
    return null;
}

/**
 * Build a DecisionRationale record for a single compound, using an
 * LLM to rewrite the two prose fields. The four numeric fields stay
 * deterministic. On any LLM failure the function returns the
 * deterministic template result with `groundedFallback: true` set.
 *
 * Schema-extended (NOT modified): consumers who don't read the new
 * fields see the same six required fields they already do.
 *
 * @param {Object} compound - Same shape as `generateFor` accepts
 * @param {Object} [context] - Same shape as `generateFor` accepts
 * @param {Object} [options] - Caller overrides
 * @param {{ sendLLMRequest: Function }} [options.llmClient]
 *   Inject for testing. Default: dynamically import the live
 *   `gibbs/text-to-action/llmClient` module.
 * @param {{ logLLMCall: Function }} [options.telemetry]
 *   Inject for testing. Default: dynamically import the live
 *   `gibbs/telemetry` module.
 * @param {string} [options.model]
 * @param {string} [options.userMessage]
 *   Short human-readable summary for the telemetry log
 *   (e.g., "explain rationale for compound 3").
 * @returns {Promise<DecisionRationale & {
 *   groundedFallback?: boolean,
 *   groundedFailureReason?: string,
 * }>}
 */
export async function generateGroundedFor(compound, context = {}, options = {}) {
    const templateResult = generateFor(compound, context);
    const start = Date.now();
    const userMessage = options.userMessage
        || `explain rationale for compound ${templateResult.compoundId}`;
    const model = options.model || DEFAULT_MODEL;

    /**
     * Helper to log + return on either success or fallback. Keeps
     * the early-return paths from each forking on telemetry.
     */
    function logAndReturn({
        resultType, parseErrors = [], validationErrors = [], merged,
    }) {
        const durationMs = Date.now() - start;
        // Fire-and-forget: never throw from the logger; never block
        // the caller on logger failures.
        try {
            (options.telemetry?.logLLMCall || lazyLogLLMCall)({
                userMessage,
                tier: 'rationale_grounding',
                systemPromptLength: SYSTEM_PROMPT.length,
                messagesCount: 2,
                model,
                responseContent: '',
                resultType,
                retryAttempt: 0,
                parseErrors,
                validationErrors,
                duration_ms: durationMs, // logLLMCall API field name
            });
        } catch (_) { /* never throw */ }
        return merged;
    }

    let send;
    try {
        send = options.llmClient?.sendLLMRequest || (await loadLiveLLMClient()).sendLLMRequest;
    } catch (err) {
        return logAndReturn({
            resultType: 'fallback',
            validationErrors: [`llm_client_load_error: ${err && err.message}`],
            merged: {
                ...templateResult,
                groundedFallback: true,
                groundedFailureReason: 'llm_client_load_error',
            },
        });
    }

    let response;
    try {
        response = await send({
            messages: [
                { role: 'system', content: SYSTEM_PROMPT },
                { role: 'user', content: buildUserPrompt(templateResult, context) },
            ],
            model,
            jsonMode: true,
        });
    } catch (err) {
        return logAndReturn({
            resultType: 'fallback',
            validationErrors: [`network_throw: ${err && err.message}`],
            merged: {
                ...templateResult,
                groundedFallback: true,
                groundedFailureReason: 'network_throw',
            },
        });
    }

    if (response && response.error) {
        return logAndReturn({
            resultType: 'fallback',
            validationErrors: [`http_error: ${response.error}`],
            merged: {
                ...templateResult,
                groundedFallback: true,
                groundedFailureReason: 'http_error',
            },
        });
    }

    // Prefer parsed JSON; fall back to parsing content ourselves.
    let parsed = response && response.parsed;
    if (!parsed) {
        try {
            parsed = JSON.parse((response && response.content) || '');
        } catch (err) {
            return logAndReturn({
                resultType: 'fallback',
                parseErrors: [`parse_error: ${err && err.message}`],
                merged: {
                    ...templateResult,
                    groundedFallback: true,
                    groundedFailureReason: 'parse_error',
                },
            });
        }
    }

    const schemaError = validateLLMProse(parsed);
    if (schemaError) {
        return logAndReturn({
            resultType: 'fallback',
            validationErrors: [`schema_invalid: ${schemaError}`],
            merged: {
                ...templateResult,
                groundedFallback: true,
                groundedFailureReason: 'schema_invalid',
            },
        });
    }

    // Anti-hallucination guard: scan the merged prose against the
    // structured input vocabulary.
    const vocab = buildAllowedVocabulary(templateResult);
    const proseToScan = `${parsed.rankExplanation}\n${parsed.suggestedNext}`;
    const halluError = detectHallucination(proseToScan, vocab, context.rank);
    if (halluError) {
        return logAndReturn({
            resultType: 'fallback',
            validationErrors: [`hallucination_caught: ${halluError}`],
            merged: {
                ...templateResult,
                groundedFallback: true,
                groundedFailureReason: 'hallucination_caught',
            },
        });
    }

    // Success path. Replace the two prose fields; preserve the four
    // numeric ones byte-for-byte from the template.
    return logAndReturn({
        resultType: 'grounded',
        merged: {
            ...templateResult,
            rankExplanation: parsed.rankExplanation,
            suggestedNext: parsed.suggestedNext,
            // Note: groundedFallback intentionally omitted on success
            // so callers can distinguish via `'groundedFallback' in result`.
        },
    });
}

/**
 * Lazy-load the live LLM client. Kept as a separate function so
 * tests can stub `options.llmClient` without forcing a real
 * dynamic import.
 * @private
 */
async function loadLiveLLMClient() {
    return import('../text-to-action/llmClient');
}

/**
 * Lazy-load the live telemetry logger. Same reasoning as
 * `loadLiveLLMClient`.
 * @private
 */
function lazyLogLLMCall(entry) {
    // Use a non-blocking dynamic import so the logger never holds up
    // the caller. The logger itself is fire-and-forget.
    import('../telemetry/llmLogger').then((m) => m.logLLMCall(entry)).catch(() => { /* never throw */ });
}
